// src/pages/ManageLocalFiles/DeleteLocalFileTemplates.js

import {
  PencilIcon,
  TrashIcon,
  DownloadIcon,
  RefreshIcon, // Import for loading state
  CogIcon, // Add CogIcon for manage
} from '@heroicons/react/outline';
import { collection, deleteDoc, doc, getDocs, onSnapshot, query } from 'firebase/firestore';
import { deleteObject, ref } from 'firebase/storage';
import React, { useEffect, useRef, useState } from 'react';
import { db, storage, shortify, useOutsideClick } from '../../firebase';
import { saveAs } from 'file-saver'; // Import saveAs
import { toast } from 'react-toastify';

function DeleteAndDownloadLocalFileTemplates({ selectedCompany }) {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState();
  const [templatesVisible, setTemplatesVisible] = useState(false);
  const [templateType, setTemplateType] = useState('Localfile');
  const [isDownloading, setIsDownloading] = useState(false); // Add downloading state

  const templatesRef = useRef();
  useOutsideClick(templatesRef, () => setTemplatesVisible(false));

  /// Handle Loading Data ///
  useEffect(() => {
    // Reset selection when company changes or type changes
    setSelectedTemplate(null);
    setIsDownloading(false); // Reset download state

    if (!selectedCompany?.id) {
      setTemplates([]);
      return; // Exit if no company selected
    }

    let colPath = '';
    if (templateType === 'Localfile') {
      colPath = collection(db, 'user_data', selectedCompany.id, 'templates');
    } else if (templateType === 'Document') {
      colPath = collection(db, 'user_data', selectedCompany.id, 'special_templates');
    } else {
      setTemplates([]);
      return; // Exit if invalid type
    }

    const unsub = onSnapshot(
      query(colPath),
      (querySnapshot) => {
        // Sort templates: those with uploadedAt first (newest to oldest), 
        // then those without uploadedAt
        const sortedDocs = querySnapshot.docs.sort((a, b) => {
          const aDate = a.data().uploadedAt;
          const bDate = b.data().uploadedAt;
          
          // Both have dates: sort by date descending
          if (aDate && bDate) {
            return bDate.localeCompare(aDate);
          }
          // Only b has date: b comes first
          if (!aDate && bDate) return 1;
          // Only a has date: a comes first  
          if (aDate && !bDate) return -1;
          // Neither has date: maintain original order
          return 0;
        });
        setTemplates(sortedDocs);
      },
      (error) => {
        console.error(`Error fetching ${templateType} templates:`, error);
        setTemplates([]); // Clear templates on error
      }
    );

    // Return the unsub function to clean up the listener
    return () => unsub();
  }, [selectedCompany, templateType]);

  /// Delete Template ///
  async function deleteTemplate() {
    if (!selectedTemplate || !selectedCompany?.id) return;

    const templateData = selectedTemplate.data();
    const docId = selectedTemplate.id;
    let collectionName = '';
    let storageFolderName = '';

    if (templateType === 'Localfile') {
      collectionName = 'templates';
      storageFolderName = 'templates-localfile';
    } else if (templateType === 'Document') {
      collectionName = 'special_templates';
      storageFolderName = 'templates-idr';
    } else {
      console.error('Invalid template type for deletion');
      return;
    }

    try {
      // 1. Delete Firestore document
      await deleteDoc(doc(db, 'user_data', selectedCompany.id, collectionName, docId));

      // 2. Delete file from Storage (if storageId exists)
      if (templateData?.storageId) {
        const filePath = `${selectedCompany.id}/${storageFolderName}/${templateData.storageId}`;
        const fileRef = ref(storage, filePath);
        try {
          await deleteObject(fileRef);
          console.log('File deleted from storage:', filePath);
        } catch (storageError) {
          // Log error but continue, Firestore doc is already deleted
          console.warn(
            'Could not delete file from storage (might have been deleted already):',
            storageError
          );
        }
      } else {
        console.warn('No storageId found for template, cannot delete from storage.');
      }

      // 3. Reset selection
      setSelectedTemplate(null);
      // The onSnapshot listener will update the list automatically
      toast.success(`Template '${templateData?.name || 'template'}' deleted successfully`);
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error(`Failed to delete template: ${error.message}`);
    }
  }

  /// Download Template ///
  async function handleDownload() {
    if (!selectedTemplate || !selectedTemplate.data()?.url || isDownloading) {
      console.log('Download pre-check failed:', {
        selectedTemplate,
        url: selectedTemplate?.data()?.url,
        isDownloading,
      });
      return;
    }

    const url = selectedTemplate.data().url;
    // Use the name stored in Firestore, default to 'template.docx' if missing
    const filename = selectedTemplate.data().name || `${templateType.toLowerCase()}_template.docx`;

    setIsDownloading(true);
    console.log(`Attempting to download: ${filename} from ${url}`);

    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const blob = await response.blob();
      saveAs(blob, filename); // Use saveAs to trigger download with the correct name
      console.log('Download initiated successfully for:', filename);
    } catch (error) {
      console.error('Error downloading template:', error);
      alert(`Failed to download template: ${error.message}`); // Provide user feedback
    } finally {
      setIsDownloading(false);
    }
  }

  /// Format date helper for yyyymmddThh:mm
  const formatUploadDate = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}${month}${day}T${hours}:${minutes}`;
  };

  function selectTemplate(template) {
    setSelectedTemplate(template);
    setTemplatesVisible(false); // Close dropdown after selection
  }

  /// HTML Component ///
  return (
    <div className="component-container w-[80%] lg:w-[55%] xl:w-[40%] relative z-10">
      <div className="component-icon-container bg-red-400">
        <CogIcon className="w-8 h-8" />
      </div>
      <p className="text-2xl p-1 m-1">Manage Topic Templates</p>
      <div className="mt-3">
        {/* Template Type Selection */}
        <div className="flex mb-2">
          <button
            onClick={() => setTemplateType('Localfile')}
            className={
              'w-[50%] rounded-lg border hover:bg-blue-500 hover:text-white border-blue-500 p-1 mx-2 transition-all ' +
              (templateType === 'Localfile' ? ' bg-blue-500 text-white' : ' text-blue-500')
            }
          >
            Topic Template
          </button>
          <button
            onClick={() => setTemplateType('Document')}
            className={
              'w-[50%] rounded-lg border hover:bg-blue-500 hover:text-white border-blue-500 p-1 mr-2 transition-all ' +
              (templateType === 'Document' ? ' bg-blue-500 text-white' : ' text-blue-500')
            }
          >
            IDR Template
          </button>
        </div>

        {/* Template Selection Dropdown */}
        <div ref={templatesRef} className="w-full z-30 relative mx-2 mb-2">
          <button
            className="w-[calc(100%-1rem)] border rounded-lg p-2 text-left"
            onClick={() => setTemplatesVisible((prev) => !prev)}
          >
            {selectedTemplate ? shortify(selectedTemplate.data()?.name, 40) : 'Select Template'}
          </button>
          <div
            className={
              'bg-white absolute w-[calc(100%-1rem)] px-1 pt-1 shadow-basic rounded-lg z-40 mt-1 max-h-48 overflow-auto ' +
              (templatesVisible ? '' : 'hidden')
            }
          >
            {templates?.length > 0 ? (
              templates.map(
                (
                  template // Removed index, using template.id as key is better
                ) => (
                  <button
                    key={template.id} // Use unique document ID as key
                    className={
                      `select-btn hover:bg-blue-500 w-full text-left ` + // Ensure full width and left align
                      (selectedTemplate?.id === template.id ? ` bg-blue-500 text-white` : '')
                    }
                    onClick={() => selectTemplate(template)}
                  >
                    <span>{template.data()?.name || 'Unnamed Template'}</span>
                    {template.data()?.uploadedAt && (
                      <span className="text-xs text-gray-500 ml-2">
                        ({formatUploadDate(template.data().uploadedAt)})
                      </span>
                    )}
                  </button>
                )
              )
            ) : (
              <p className="p-2 text-sm text-gray-500">No templates found.</p>
            )}
          </div>
        </div>

        {/* Action Buttons: Download and Delete */}
        <div className="flex justify-end mt-2 mx-2 space-x-2">
          {selectedTemplate && (
            <>
              {/* Download Button */}
              <button
                onClick={handleDownload}
                disabled={isDownloading || !selectedTemplate.data()?.url}
                className={`p-2 text-white rounded-lg transition-all flex items-center justify-center ${
                  isDownloading || !selectedTemplate.data()?.url
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-green-500 hover:bg-green-600'
                }`}
                title={`Download ${selectedTemplate.data()?.name || 'template'}`}
              >
                {isDownloading ? (
                  <RefreshIcon className="w-5 h-5 animate-spin" />
                ) : (
                  <DownloadIcon className="w-5 h-5" />
                )}
              </button>

              {/* Delete Button */}
              <button
                disabled={isDownloading} // Optionally disable delete while downloading
                className={`p-2 text-white rounded-lg transition-all ${
                  isDownloading ? 'bg-gray-400 cursor-not-allowed' : 'bg-red-400 hover:bg-red-500'
                }`}
                onClick={deleteTemplate}
                title={`Delete ${selectedTemplate.data()?.name || 'template'}`}
              >
                <TrashIcon className="w-5 h-5" />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default DeleteAndDownloadLocalFileTemplates;
